// Ponte entre o histórico de faturas do rateio e o cálculo puro de projeção.
//
// `src/lib/projecaoConsumo.ts` é puro e trabalha em ordinais de mês, justamente
// para não depender de `views/`. Este arquivo faz a tradução no sentido único
// permitido (views → src/lib) e concentra as decisões sujas do dado real:
// mês em formato livre, fatura duplicada e ausência de injeção confiável.
//
//   node --test views/Gestao/Rateio/__tests__/projecaoUc.test.ts

import {
  calcularProjecaoConsumo,
  type ObservacaoMensal,
  type ResultadoProjecao,
} from '../../../src/lib/projecaoConsumo.ts';
import { limparObservacoes } from '../../../supabase/functions/_shared/consumoObservado.ts';
// Só o tipo — `consumoHistorico.ts` importa o cliente Supabase, e um import de
// valor traria rede/env para dentro do teste. `import type` some na compilação.
import type { FaturaConsumoResumo } from './consumoHistorico.ts';

export interface ObservacoesDeFaturas {
  observacoes: ObservacaoMensal[];
  /**
   * Faturas descartadas por já existir outra do mesmo mês para a mesma UC.
   * Contar o mesmo mês duas vezes daria peso dobrado a ele na média — e fatura
   * duplicada acontece de verdade (ver `FIN-R09`, a regra mais retrabalhada do
   * sistema). O número é exposto para a tela poder avisar em vez de esconder.
   */
  duplicatasIgnoradas: number;
  /** Faturas cujo `mes_referencia` não foi reconhecido — nunca somem caladas. */
  mesesNaoReconhecidos: number;
}

/**
 * Converte o retorno de `buscarHistoricoConsumoUcs` em observações mensais.
 *
 * NÃO preenche `injecaoKwh`: em `faturas`, `energia_injetada_gd1_kwh` e
 * `injecao_kwh_ug_*` são o crédito que as geradoras da VIGOR injetam na UC —
 * semântica oposta à do cálculo, que quer a GD DO PRÓPRIO CLIENTE (é o que a
 * análise técnica digita a partir do portal da distribuidora). Preencher um com
 * o outro inverteria o sinal do compensável. Quem tiver a injeção própria da UC
 * de fonte confiável passa `injecaoKwh` por fora.
 */
export function observacoesDeFaturas(historico: FaturaConsumoResumo[] | null | undefined): ObservacoesDeFaturas {
  // Delegado a `_shared/` de propósito: é a MESMA função que a rotina diária usa
  // para gravar `consumo_observado_kwh`. Reimplementar aqui faria a tela e o job
  // divergirem no primeiro ajuste de filtro.
  return limparObservacoes(historico);
}

export interface ProjecaoUc extends ResultadoProjecao {
  duplicatasIgnoradas: number;
  mesesNaoReconhecidos: number;
}

/**
 * Projeção de uma UC a partir do histórico de faturas já carregado pelo rateio.
 * Os avisos das duas camadas (tradução e cálculo) chegam juntos em `avisos`.
 */
export function projetarConsumoUc({
  historico,
  ordinalAtual,
  horizonteMeses,
  tipoLigacao,
}: {
  historico: FaturaConsumoResumo[] | null | undefined;
  ordinalAtual: number;
  horizonteMeses?: number;
  tipoLigacao?: string | null;
}): ProjecaoUc {
  const { observacoes, duplicatasIgnoradas, mesesNaoReconhecidos } = observacoesDeFaturas(historico);

  const resultado = calcularProjecaoConsumo({
    observacoes,
    ordinalAtual,
    horizonteMeses,
    tipoLigacao,
    // Sem fonte confiável da GD própria do cliente aqui — ver nota em
    // `observacoesDeFaturas`. O compensável sai sem esse desconto.
    possuiGD: false,
  });

  const avisos = [...resultado.avisos];
  if (duplicatasIgnoradas > 0) {
    avisos.push(
      `${duplicatasIgnoradas} ${duplicatasIgnoradas === 1 ? 'fatura duplicada foi ignorada' : 'faturas duplicadas foram ignoradas'} ` +
        '(mesmo mês de referência já contabilizado).',
    );
  }
  if (mesesNaoReconhecidos > 0) {
    avisos.push(
      `${mesesNaoReconhecidos} ${mesesNaoReconhecidos === 1 ? 'fatura ficou' : 'faturas ficaram'} de fora ` +
        'por mês de referência ou consumo ilegível.',
    );
  }

  return { ...resultado, avisos, duplicatasIgnoradas, mesesNaoReconhecidos };
}
